import { startAuthentication } from '@simplewebauthn/browser';
import { useEffect, useMemo, useState } from 'react';
import { begin_login, finish_login } from '../api/kernel_client';
import { CeremonyChrome } from '../components/ceremony_chrome';
import { get_translations } from '../i18n/translations';
import { probe_passkey_support } from '../lib/capability_probe';
import { read_popup_params } from '../lib/url_params';
import { send_to_opener } from '../protocol/messenger';

type CeremonyState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const CEREMONY_TIMEOUT_MS = 60_000;

export function PopupLoginRoute() {
  const t = useMemo(() => get_translations(), []);
  const params = useMemo(() => read_popup_params(window.location.search), []);
  const [supported, set_supported] = useState<boolean | null>(null);
  const [state, set_state] = useState<CeremonyState>({ kind: 'idle' });

  useEffect(() => {
    void probe_passkey_support().then((ok) => set_supported(ok));
  }, []);

  useEffect(() => {
    if (params === null) return;
    const opener = window.opener;
    if (opener === null) return;
    send_to_opener(opener, params.return_origin, { type: 'ready', request_id: params.request_id });
  }, [params]);

  useEffect(() => {
    if (state.kind !== 'running') return undefined;
    const handle = window.setTimeout(() => {
      if (params !== null) {
        const opener = window.opener;
        if (opener !== null) {
          send_to_opener(opener, params.return_origin, {
            type: 'cancel',
            request_id: params.request_id,
            reason: 'timeout',
          });
        }
      }
      set_state({ kind: 'error', message: t.error_timeout });
    }, CEREMONY_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [state.kind, params, t.error_timeout]);

  async function run_ceremony() {
    if (params === null) return;
    set_state({ kind: 'running' });
    try {
      const opts = await begin_login({
        ...(params.username !== undefined ? { username: params.username } : {}),
        api_client_id: params.api_client_id,
        return_origin: params.return_origin,
      });
      const credential = await startAuthentication({
        optionsJSON: opts.public_key as unknown as Parameters<
          typeof startAuthentication
        >[0]['optionsJSON'],
      });
      const result = await finish_login({ challenge_id: opts.challenge_id, credential });
      const opener = window.opener;
      if (opener !== null) {
        send_to_opener(opener, params.return_origin, {
          type: 'success',
          request_id: params.request_id,
          sso_ticket: result.sso_ticket,
        });
      }
      set_state({ kind: 'success' });
      window.setTimeout(() => window.close(), 250);
    } catch (e) {
      const message = e instanceof Error ? e.message : t.error_unknown;
      const code =
        message === 'CANCELLED' || /cancel|abort/i.test(message) ? 'user_cancelled' : 'failed';
      const opener = window.opener;
      if (opener !== null && params !== null) {
        if (code === 'user_cancelled') {
          send_to_opener(opener, params.return_origin, {
            type: 'cancel',
            request_id: params.request_id,
            reason: 'user_denied',
          });
        } else {
          send_to_opener(opener, params.return_origin, {
            type: 'error',
            request_id: params.request_id,
            code: 'WEBAUTHN_FAILED',
            message,
          });
        }
      }
      set_state({
        kind: 'error',
        message: code === 'user_cancelled' ? t.error_user_cancelled : message,
      });
    }
  }

  if (params === null) {
    return <CeremonyChrome title={t.app_title} body={<p>Invalid launch parameters.</p>} />;
  }
  if (supported === false) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.error_no_support}</p>} />;
  }
  if (supported === null) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.waiting}</p>} />;
  }

  return (
    <CeremonyChrome
      title={t.app_title}
      body={
        <>
          <p style={{ fontSize: '14px', color: '#545454', marginBottom: '12px' }}>{t.waiting}</p>
          {state.kind === 'idle' || state.kind === 'error' ? (
            <button onClick={run_ceremony} style={primary_button_style}>
              {t.use_passkey}
            </button>
          ) : null}
          {state.kind === 'running' ? <p style={{ color: '#545454' }}>{t.waiting}</p> : null}
          {state.kind === 'success' ? <p style={{ color: '#0a7c1f' }}>{t.success}</p> : null}
          {state.kind === 'error' ? (
            <p role="alert" style={{ color: '#b00020' }}>
              {state.message}
            </p>
          ) : null}
        </>
      }
    />
  );
}

const primary_button_style: React.CSSProperties = {
  background: '#BCCF17',
  color: '#1a1a1a',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};
